//#region import libraries
import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

// custom components
import TimelineHourRow from "../timeline-components/timeline-hour-row";
import FlightInformation from "./flight-information";
import JobCard from "./job-card";
import TimelineThirtyMinHourRow from "../timeline-components/timeline-thirtyMin-hour-row";

// date 
import moment from 'moment';

// redux
import { useSelector, useDispatch } from "react-redux";
import { selectIata } from '_redux/slices/main-screen/user';
import { selectBookingsByFlight, selectPostedBookingsStatus, selectBookingSearch, selectFlightsType, selectShowAddPassenger, setShowAddPassenger } from "_redux/slices/timeline/timeline-data";
import { resetPostedBooking, resetAdditionalPassengers } from '_redux/slices/prm-bookings/bookings';
import { selectOutboundFlights, selectFlightsByHour, selectInboundFlights, selectExpandedFlights, setExpandedFlights, selectSearchedFlight, setSearchedFlight } from '_redux/slices/timeline/flight-information-list';
import { selectExpandAllFlights, selectThirtyMinLayout, selectTimelineDate } from "_redux/slices/timeline/timeline-control";

// styling
import { Spacing } from "_styles";

// constants
import { EmployeeInformationColumnWidth, EmployeeInformationColumnWidthRaw, TimelineColumnWidth, Timeline30MinLayoutColumnWidth } from "../../../../utils/timeline-constants";
import { formatTimeToNearest30 } from "_utils/functions";


const List = styled.div`
  flex-grow: 1;
`;

const JobColumn = styled.div`
  min-width: ${props => (props.minWidth)};
  min-height: 150px;
  border: 1px solid lightgrey;
`;

const FlightListContainer = styled.div`
  display: flex;
  justify-content: center;
`;

//#endregion

const TimelineJobs = (props) => {

  const flightRefs = useRef({});

  //#region redux
  const dispatch = useDispatch()

  const iata = useSelector(selectIata);                                       // holds iata of account

  // const bookingsByFlight = useSelector(selectBookingsByFlight)                         // list of bookings grouped by their flight
  const bookingsByFlight = useSelector(selectBookingsByFlight)                         // list of bookings grouped by their flight
  // same as selectBookingsByFlight but without any delete on bookings when it is assigned to pass in to flight information when there is no flight feed

  const allBookingsByFlight = useSelector((state) => selectBookingsByFlight(state, true))

  const flightsByHour = useSelector(selectFlightsByHour)                               // list of flights grouped by hour

  const postedBookingStatus = useSelector(selectPostedBookingsStatus)         // status of post request

  const outboundFlights = useSelector(selectOutboundFlights)                   // list of flights
  const inboundFlights = useSelector(selectInboundFlights)                    // list of flights
  const expandedFlights = useSelector(selectExpandedFlights)                    // list of expanded flights
  const expandAllFlights = useSelector(selectExpandAllFlights);
  const bookingSearch = useSelector(selectBookingSearch);
  const searchedFlight = useSelector(selectSearchedFlight);

  const thirtyMinLayout = useSelector(selectThirtyMinLayout);
  const flightsType = useSelector(selectFlightsType); // select flightsType (international,domestic,all) mainly for SGS
  const timelineDate = useSelector(selectTimelineDate);                                 // select timeline Date

  //#endregion

  //#region local states
  const [clicked, setClicked] = useState([]);                           // if job is clicked or not
  const [timelineHour, setTimelineHour] = useState(0);                  // holds current hour

  const [timelinePercentage, setTimelinePercentage] = useState(0);      // holds position of line relative to minute in hour
  const [selectedFlightsByHour, setSelectedFlightsByHour] = useState({})
  const [timelineJobs, setTimelineJobs] = useState(null);
  const [flightHasEmptyPRMs, setFlightHasEmptyPRMs] = useState([])
  //#endregion

  //#region useEffect



  useEffect(() => {
    dispatch(setShowAddPassenger(null));

    dispatch(resetPostedBooking())
    dispatch(resetAdditionalPassengers())
  }, []);

  useEffect(() => {
    // check if expandAllFlights switch is turned on return 
    if (expandAllFlights) return;
    // get the current time
    const now = moment();

    // format the hours to match the keys in the flightsByHour object (e.g., "14:00")
    const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`;

    // get the current hour in 24-hour format
    const currentHour = formatHour(now.hour());

    // get one hour before
    const oneHourBefore = formatHour(now.clone().subtract(1, 'hour').hour());

    // get one hour after
    const oneHourAfter = formatHour(now.clone().add(1, 'hour').hour());

    // check if the object flightsByHour all it's keys are empty arrays
    if (!Object.isObjectEmpty(flightsByHour)) {
      let tempExpandedFlights = [];
      // extract the arrays from your object
      const previousHourFlights = flightsByHour[oneHourBefore] || [];
      // check if the previousHourFlights is an array with length push each flight with the hour key to the  tempExpandedFlights
      if (Array.isFullArray(previousHourFlights)) {
        previousHourFlights?.forEach((flight) => {
          tempExpandedFlights.push(`${flight}_${oneHourBefore}`);
        })
      }
      const currentHourFlights = flightsByHour[currentHour] || [];
      if (Array.isFullArray(currentHourFlights)) {
        currentHourFlights?.forEach((flight) => {
          tempExpandedFlights.push(`${flight}_${currentHour}`);
        })
      }
      const nextHourFlights = flightsByHour[oneHourAfter] || [];
      if (Array.isFullArray(nextHourFlights)) {
        nextHourFlights?.forEach((flight) => {
          tempExpandedFlights.push(`${flight}_${oneHourAfter}`);
        })
      }
      if (Array.isFullArray(tempExpandedFlights)) {
        dispatch(setExpandedFlights(tempExpandedFlights));
      }

    };
  }, []);

  // on render get flight list

  // monitor patch status
  useEffect(() => {
    if (postedBookingStatus == 'succeeded') {
      // show success modal
      dispatch(setShowAddPassenger(null));

      dispatch(resetAdditionalPassengers());
    }
  }, [postedBookingStatus]);


  useEffect(() => {
    setSelectedFlightsByHour(flightsByHour);
  }, [iata, flightsByHour, flightsType]);


  useEffect(() => {
    // only proceed if there's a booking search but no flight search
    if (bookingSearch && !searchedFlight) {
      const debounceTimer = setTimeout(() => {
        // proceed only if bookingsByFlight is not empty
        if (!Object.isObjectEmpty(bookingsByFlight)) {
          let tempFlight = Object.keys(bookingsByFlight)?.[0]?.split("_")?.[0] ?? "not found";

          dispatch(setSearchedFlight(tempFlight));
        } else {
          // if bookingsByFlight is empty, set searchedFlight to "not found" so no flights show up
          // this is to prevent the search from showing all flights when there is no booking
          dispatch(setSearchedFlight("not found"));
        }
      }, 500); // wait 500 ms after last change

      return () => clearTimeout(debounceTimer);
    }
  }, [bookingsByFlight, bookingSearch, searchedFlight]);


  //#region scroll handling

  //This useEffect works while booking search is enabled
  useEffect(() => {
    if (
      bookingSearch !== "" &&
      bookingSearch?.length &&
      bookingsByFlight &&
      Object.values(bookingsByFlight)?.length &&
      selectedFlightsByHour &&
      Object.values(selectedFlightsByHour)?.length
    ) {
      const year = new Date().getFullYear();
      const bookingIds = Object.values(bookingsByFlight)?.flat().map(booking => booking._id);


      if (bookingIds.length && bookingSearch.length > 2) {
        const paxName = bookingIds.map(bid => bid.split("--")[1]?.replace("_", " "))
        const matchedBookings = bookingIds.filter(bid => bid.includes(bookingSearch) || paxName?.includes(bookingSearch.toLowerCase()));

        if (matchedBookings.length) {
          // Extract cc+flight number from matched bookings
          const bookingFlights = matchedBookings.map(booking => booking.split(`${year}`)[0]);

          // Filter flights that contain any bookingFlight while keeping the hour structure
          const updatedFlightsByHour = Object.entries(selectedFlightsByHour).reduce((acc, [hour, flights]) => {
            const matchedFlights = flights.filter(flight =>
              bookingFlights.some(bookingFlight => flight.includes(bookingFlight))
            );
            acc[hour] = matchedFlights.length ? matchedFlights : [];
            return acc;
          }, {});
          setSelectedFlightsByHour(updatedFlightsByHour);
        }
      }
    }
  }, [bookingSearch, bookingsByFlight, flightsType, searchedFlight]);

  // Scroll effect
  useEffect(() => {
    if (bookingSearch) {
      let timer = setTimeout(() => {
        const firstFlightKey = Object.keys(bookingsByFlight).find(
          key => bookingsByFlight[key]?.length > 0
        );

        if (firstFlightKey) {
          const flightElement = flightRefs.current[firstFlightKey];

          if (flightElement) {
            if (props.disableScrollSync) props.disableScrollSync.current = true; // temporarily disable scroll syncing

            flightElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest",
            });

            setTimeout(() => {
              if (props.disableScrollSync) props.disableScrollSync.current = false; // re-enable scroll syncing after scrolling completes
            }, 500);
          } else {
            console.warn(`Element not found in DOM for: ${firstFlightKey}`);
          }
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [bookingSearch, bookingsByFlight]);

  useEffect(() => {
    setTimelineJobs(bookingsByFlight)
  }, [bookingsByFlight, bookingSearch])

  useEffect(() => {
    const flightsWithNoJob = [];
    const jobsKeys = Object.keys(bookingsByFlight);
    if (typeof selectedFlightsByHour !== "object" || !Object.keys(selectedFlightsByHour)?.length) return;
    Object.entries(selectedFlightsByHour).forEach(([hour, flights]) => {
      const hourNumber = hour.split(":")[0]; // e.g., "10"

      flights.forEach(flight => {
        const key = `${flight}_${hourNumber}`;
        const found = jobsKeys.includes(key);

        if (!found) {
          flightsWithNoJob.push(key); // or just flight
        }
      });
      setFlightHasEmptyPRMs(flightsWithNoJob)

    });

  }, [bookingsByFlight, selectedFlightsByHour])


  //#endregion


  useEffect(() => {
    const updateTimeline = () => {
      const currentDate = new Date();

      // Get current hour and minute
      const currentHour = currentDate.getHours();
      const currentMinute = currentDate.getMinutes();
      if (thirtyMinLayout) {
        // Format the time to the nearest 30-minute block
        const formattedTime = formatTimeToNearest30();
        setTimelineHour(formattedTime);

        // Calculate the percentage within the 30-minute block
        const currentMinuteInBlock = currentMinute % 30;
        const percentage = (currentMinuteInBlock * 100) / 30;
        setTimelinePercentage(percentage);
      } else {
        setTimelineHour(timeFormatter.formatHour(currentHour));
        let minuteWidth = EmployeeInformationColumnWidthRaw / 60 + 0.5; // 1 minute width in pixels
        let marginLeft = minuteWidth * currentMinute; // margin left in pixels
        setTimelinePercentage(marginLeft); // set the percentage for the timeline line
        //setTimelinePercentage((currentMinute * 100) / 60);
      }

    };
    const timeFormatter = {
      formatHour: (hour) => {
        return `${String(hour).padStart(2, '0')}:00`;
      }
    };

    // Initial update
    updateTimeline();

    // Schedule updates every 60 seconds
    const intervalId = setInterval(updateTimeline, 60000);

    // Clean up the interval on component unmount
    return () => clearInterval(intervalId);
  }, []);


  function isSameDay(d1, d2) {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }
  //#endregion

  return (
    <div>
      {thirtyMinLayout
        ?
        <List>

          {/* employee column spacer */}
          <TimelineThirtyMinHourRow backgroundColor={"transparent"} />

          {/* map a column for each hour in the timeline and the job list for that hour */}
          <div style={{ display: "flex" }}>
            {/* employee column spacer */}
            <div style={{ minWidth: EmployeeInformationColumnWidth, maxWidth: EmployeeInformationColumnWidth, position: "sticky", left: 0 }} />

            {/* listing each hour + the flight at that hour */}
            {!Object.isObjectEmpty(selectedFlightsByHour) &&

              Object.keys(selectedFlightsByHour).map((hour, i) => (

                <JobColumn key={i}
                  style={{ position: 'relative' }}
                  // style={{ position: 'relative', background: timelineHour == hour && `linear-gradient(red, red) no-repeat ${timelinePercentage}% center/2px 100% z-index: 2` }}
                  minWidth={Timeline30MinLayoutColumnWidth}>
                  {timelineHour == hour &&
                    <div style={{
                      height: '100%',
                      width: 2,
                      backgroundColor: 'red',
                      position: 'absolute',
                      marginLeft: timelinePercentage + 30,//TODO,review this value, 
                      top: 0
                    }} />
                  }
                  {/* map each flight */}
                  {selectedFlightsByHour[hour].map((flight, l) => (

                    <div ref={(el) => (flightRefs.current[`${flight}_${hour}`] = el)} style={{ borderWidth: 1, borderStyle: 'solid', borderColor: "lightgrey", marginTop: "3px" }} key={l}>

                      {/* flight number */}
                      <FlightInformation
                        flight={flight}
                        oneBooking={(!["JER", "DUB", "ORK", "BFS", "SNN", "PUJ"].includes(iata) || (!Array.isFullArray(outboundFlights) && !Array.isFullArray(inboundFlights)))
                          ? allBookingsByFlight?.[flight + "_" + hour]?.[0]
                          : null}
                        flightToExpand={flight + "_" + hour}
                        thirtyMinLayout={thirtyMinLayout}
                        data-tippy-content="Flight Number"
                        hasEmptyPRMs={flightHasEmptyPRMs.includes(`${flight}_${hour.slice(0, 2)}`)}
                      />

                      {/* expandAllFlights is on return show all else show if expanded only*/}
                      {expandAllFlights ?
                        <FlightListContainer>

                          {/* map each job that matches the flight */}
                          <div style={{ zIndex: 1 }}>
                            {timelineJobs[flight + "_" + hour] != undefined &&
                              timelineJobs[flight + "_" + hour].map != undefined &&
                              timelineJobs[flight + "_" + hour]?.map((job, index) => (<div style={{ display: "flex", marginTop: index == 0 ? Spacing.SCALE_2 : -Spacing.SCALE_5 }} key={index}>
                                <JobCard
                                  job={job}
                                  index={index}
                                  clicked={clicked}
                                  setClicked={setClicked}
                                  timelineJobs={true}     // card is being displayed in timeline jobs section
                                  outboundFlights={Array.isFullArray(outboundFlights) ? outboundFlights : []}
                                />
                              </div>
                              ))}
                          </div>
                        </FlightListContainer>
                        :
                        Array.isFullArray(expandedFlights) && expandedFlights?.includes(flight + "_" + hour) && <FlightListContainer>

                          {/* map each job that matches the flight */}
                          <div style={{ zIndex: 1 }}>
                            {timelineJobs[flight + "_" + hour] != undefined &&
                              timelineJobs[flight + "_" + hour].map != undefined &&
                              timelineJobs[flight + "_" + hour]?.map((job, index) => (
                                <div style={{ display: "flex", marginTop: index == 0 ? Spacing.SCALE_8 : -Spacing.SCALE_5 }} key={index}>
                                  <JobCard
                                    job={job}
                                    clicked={clicked}
                                    setClicked={setClicked}
                                    timelineJobs={true}
                                    thirtyMinLayout={thirtyMinLayout}
                                    position={'unset'}

                                  />
                                </div>
                              ))}
                          </div>
                        </FlightListContainer>
                      }
                    </div>
                  ))}
                </JobColumn>
              ))}
          </div>
        </List>
        :
        <List>

          {/* employee column spacer */}
          <TimelineHourRow />

          {/* map a column for each hour in the timeline and the job list for that hour */}
          <div style={{ display: "flex" }}>
            {/* employee column spacer */}
            <div style={{ minWidth: EmployeeInformationColumnWidth, maxWidth: EmployeeInformationColumnWidth, position: "sticky", left: 0 }} />

            {/* listing each hour + the flight at that hour */}
            {!Object.isObjectEmpty(selectedFlightsByHour) &&

              Object.keys(selectedFlightsByHour).map((hour, i) => (

                <JobColumn key={i} style={{ position: 'relative' }} minWidth={TimelineColumnWidth}>

                  {timelineHour == hour && isSameDay(new Date(), timelineDate) &&
                    <div style={{
                      height: '100%',
                      width: 2,
                      backgroundColor: 'red',
                      position: 'absolute',
                      marginLeft: timelinePercentage,
                      top: 0
                    }} />
                  }

                  {/* map each flight */}
                  {selectedFlightsByHour[hour].map((flight, l) => (

                    <div ref={(el) => (flightRefs.current[`${flight}_${hour.slice(0, 2)}`] = el)} style={{ borderWidth: 1, borderStyle: 'solid', borderColor: "lightgrey", marginTop: "3px" }} key={l}>
                      {/* flight number */}
                      <FlightInformation
                        flight={flight}
                        oneBooking={(!["JER", "DUB", "ORK", "BFS", "SNN", "PUJ"].includes(iata) || (!Array.isFullArray(outboundFlights) && !Array.isFullArray(inboundFlights)))
                          ? allBookingsByFlight?.[flight + "_" + hour?.slice(0, 2)]?.[0]
                          : null}
                        flightToExpand={flight + "_" + hour}
                        hasEmptyPRMs={flightHasEmptyPRMs.includes(`${flight}_${hour.slice(0, 2)}`)} />

                      {/* expandAllFlights is on return show all else show if expanded only*/}
                      {expandAllFlights ?
                        <FlightListContainer>

                          {/* map each job that matches the flight */}
                          <div style={{ zIndex: 1 }}>
                            {timelineJobs[flight + "_" + hour?.slice(0, 2)] != undefined &&
                              timelineJobs[flight + "_" + hour?.slice(0, 2)].map != undefined &&
                              timelineJobs[flight + "_" + hour?.slice(0, 2)]?.map((job, index) => (
                                <div style={{
                                  display: "flex",
                                  marginTop: index == 0 ? Spacing.SCALE_2 : -Spacing.SCALE_5
                                  //borderStyle:'solid', borderWidth:1
                                }} key={index}>
                                  <JobCard
                                    job={job}
                                    index={index}
                                    clicked={clicked}
                                    setClicked={setClicked}
                                    timelineJobs={true}     // card is being displayed in timeline jobs section
                                    outboundFlights={Array.isFullArray(outboundFlights) ? outboundFlights : []}
                                  />
                                </div>
                              ))}
                          </div>
                        </FlightListContainer>
                        :
                        Array.isFullArray(expandedFlights) && expandedFlights?.includes(flight + "_" + hour) && <FlightListContainer>

                          {/* map each job that matches the flight */}
                          <div style={{ zIndex: 1 }}>
                            {timelineJobs[flight + "_" + hour?.slice(0, 2)] != undefined &&
                              timelineJobs[flight + "_" + hour?.slice(0, 2)].map != undefined &&
                              timelineJobs[flight + "_" + hour?.slice(0, 2)]?.map((job, index) => (
                                <div style={{
                                  display: "flex",
                                  marginTop: index == 0 ? Spacing.SCALE_2 : -Spacing.SCALE_5
                                  //borderStyle:'solid', borderWidth:1
                                }} key={index}>
                                  <JobCard
                                    job={job}
                                    index={index}
                                    clicked={clicked}
                                    setClicked={setClicked}
                                    timelineJobs={true}     // card is being displayed in timeline jobs section
                                    outboundFlights={Array.isFullArray(outboundFlights) ? outboundFlights : []}
                                  />
                                </div>
                              ))}
                          </div>
                        </FlightListContainer>
                      }
                    </div>
                  ))}
                </JobColumn>
              ))}
          </div>
        </List>
      }
    </div>
  );
};

export default TimelineJobs;